import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import keycloak from './keycloak';
import type { DashboardRole, DashboardUser } from '@/types/auth';

interface AuthContextValue {
  user: DashboardUser | null;
  isAuthenticated: boolean;
  isInitializing: boolean;
  login: () => void;
  logout: () => void;
  hasRole: (role: DashboardRole) => boolean;
  hasAnyRole: (roles: DashboardRole[]) => boolean;
}

// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthContextValue | null>(null);

function parseUser(kc: typeof keycloak): DashboardUser | null {
  if (!kc.authenticated || !kc.tokenParsed) return null;

  const parsed = kc.tokenParsed as Record<string, unknown>;
  const realmAccess = parsed['realm_access'] as { roles?: string[] } | undefined;
  const roles = (realmAccess?.roles ?? []).filter((r): r is DashboardRole =>
    [
      'AUTHMATECH_SUPER_ADMIN',
      'AUTHMATECH_ADMIN',
      'AUTHMATECH_OPERATOR',
      'AUTHMATECH_SUPPORT',
      'AUTHMATECH_FINANCE',
      'AUTHMATECH_VIEWER',
      'AUTHMATECH_CLIENT_ADMIN',
      'AUTHMATECH_CLIENT_OPERATOR',
      'AUTHMATECH_CLIENT_FINANCE',
      'AUTHMATECH_CLIENT_VIEWER',
    ].includes(r),
  );

  // authmatech_client_id is a custom Keycloak user-attribute claim — NOT the OAuth client_id
  const clientId = (parsed['authmatech_client_id'] as string | undefined) ?? undefined;

  return {
    id: parsed['sub'] as string,
    email: (parsed['email'] as string) ?? '',
    name: (parsed['name'] as string) ?? (parsed['preferred_username'] as string) ?? '',
    preferredUsername: (parsed['preferred_username'] as string) ?? '',
    roles,
    clientId,
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isInitializing, setIsInitializing] = useState(true);
  const [user, setUser] = useState<DashboardUser | null>(null);

  useEffect(() => {
    keycloak
      .init({
        onLoad: 'check-sso',
        pkceMethod: 'S256',
        silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
      })
      .then((authenticated) => {
        if (authenticated) {
          setUser(parseUser(keycloak));
        }
        setIsInitializing(false);
      })
      .catch(() => {
        setIsInitializing(false);
      });

    keycloak.onTokenExpired = () => {
      keycloak.updateToken(30).catch(() => {
        setUser(null);
        keycloak.login();
      });
    };

    keycloak.onAuthSuccess = () => {
      setUser(parseUser(keycloak));
    };

    keycloak.onAuthLogout = () => {
      setUser(null);
    };
  }, []);

  const login = useCallback(() => {
    keycloak.login({ redirectUri: window.location.origin });
  }, []);

  const logout = useCallback(() => {
    keycloak.logout({ redirectUri: window.location.origin });
  }, []);

  const hasRole = useCallback(
    (role: DashboardRole) => keycloak.hasRealmRole(role),
    [],
  );

  const hasAnyRole = useCallback(
    (roles: DashboardRole[]) => roles.some((r) => keycloak.hasRealmRole(r)),
    [],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isAuthenticated: !!user,
      isInitializing,
      login,
      logout,
      hasRole,
      hasAnyRole,
    }),
    [user, isInitializing, login, logout, hasRole, hasAnyRole],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
